import React, { useState, useEffect } from 'react';
import "./SuggestionProfile.css";
import { useMessengerContext } from '../../hooks/useMessengerContext';
import SwipeableViews from 'react-swipeable-views-react-18-fix';
import { useAuthContext } from '../../hooks/useAuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { getApps, initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import firebase from "../../Firebase";
import Cookies from "js-cookie";
import useSuggestionProfileView from '../../hooks/useSuggestionProfileView';

if (!getApps().length) {
  initializeApp(firebase);
}

// communityId / conversationId: for pages with no Messenger chat open (the
// /meet chat) — otherwise the open chat's.
const SuggestionProfile = ({ onBackToChatBoxForCSS, suggestionProfile, onProfileResponse, communityId, conversationId }) => {
  console.log("BASIC TEST: SuggestionProfile component loaded");
  const { state } = useMessengerContext();
  const { currentConversation, userInterests } = state;
  const { user } = useAuthContext();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [csrfToken, setCsrfToken] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasResponded, setHasResponded] = useState(false);
  const [responseType, setResponseType] = useState(null); // 'accept' or 'reject'
  // Admin can see who opened which suggested profile, for how long, and how
  // many photos they looked at (suggestionprofileviews).
  const { recordOutcome } = useSuggestionProfileView({
    viewedUserId: suggestionProfile?.userId,
    communityId: currentConversation?.communityId,
    conversationId: currentConversation?._id,
    photoIndex: currentIndex,
    photoCount: suggestionProfile?.pics?.length || 0,
  });

  // Check existing interest state and reset local state when suggestionProfile changes
  useEffect(() => {
    console.log("[profile interested checking] SuggestionProfile useEffect: Setting up state for profile:", suggestionProfile?.userId);
    
    // Check if user has already expressed interest in this profile
    const existingInterest = userInterests?.[suggestionProfile?.userId];
    
    if (existingInterest) {
      // User has already made a decision - set read-only state
      setHasResponded(true);
      setResponseType(existingInterest === 'interested' ? 'accept' : 'reject');
      console.log("[profile interested checking] Found existing interest:", existingInterest, "- setting read-only state");
    } else {
      // No existing decision - allow user to choose
      setHasResponded(false);
      setResponseType(null);
      console.log("[profile interested checking] No existing interest - allowing new decision");
    }
    
    setIsProcessing(false);
    setCurrentIndex(0);
  }, [suggestionProfile?.userId, userInterests]); // Include userInterests to react to state changes

  // Fetch CSRF token
  useEffect(() => {
    const fetchCsrfToken = async () => {
      const csrfToken = Cookies.get("mytoken");
      setCsrfToken(csrfToken);
    };
    fetchCsrfToken();
  }, []);

  if (!suggestionProfile) {
    return <div>Loading profile details...</div>;
  }

  const handleInterestResponse = async (isInterested) => {
    if (isProcessing || hasResponded) return;
    
    console.log("[profileinterest debug] 🎯 handleInterestResponse called with isInterested:", isInterested);
    console.log("[profileinterest debug] 🎯 suggestionProfile.userId:", suggestionProfile.userId);
    console.log("[profileinterest debug] 🎯 user.userId:", user.userId);
    setIsProcessing(true);
    recordOutcome(isInterested ? 'interested' : 'not_interested');
    
    try {
      const auth = getAuth();
      console.log("[profileinterest debug] Getting Firebase auth token...");
      const token = await auth.currentUser.getIdToken(true);
      console.log("[profileinterest debug] Firebase token obtained successfully");

      const requestBody = { 
        choosingUser: state.userDisplayProfile || { userId: user.userId }, // Use full profile object
        consideredUser: suggestionProfile,
        verdict: isInterested,
        communityId: communityId || currentConversation?.communityId,
      };

      console.log("[profileinterest debug] 📤 Sending API request to /interRecord/update with:", {
        choosingUserId: requestBody.choosingUser.userId,
        consideredUserId: requestBody.consideredUser.userId,
        verdict: requestBody.verdict,
        communityId: requestBody.communityId
      });

      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify(requestBody),
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/interRecord/update`,
        requestOptions
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[profile interested checking] API Error:", response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const data = await response.json();
      console.log("[profileinterest debug] 📥 SuggestionProfile API response:", data);
      console.log("[profileinterest debug] Request was for verdict:", isInterested);

      if (isInterested && data) {
        // Send acknowledgment message for interested response
        const requestBody2 = {
          choosingUser: user.userId, // This API expects just the userId
          interestedInUserName: suggestionProfile.name,
          communityId: communityId || currentConversation?.communityId,
          conversationId: conversationId || currentConversation?._id,
        };

        const requestOptions2 = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify(requestBody2),
        };

        const confirmResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/interMessaging/interestAcknowledgeMessage`,
          requestOptions2
        );

        if (confirmResponse.ok) {
          console.log("Acknowledgment message sent successfully");
        }
      }

      setHasResponded(true);
      setResponseType(isInterested ? 'accept' : 'reject');
      
      // Notify parent component about the response
      if (onProfileResponse) {
        console.log("[profileinterest debug] 🔄 SuggestionProfile calling onProfileResponse with userId:", suggestionProfile.userId, "isInterested:", isInterested);
        onProfileResponse(suggestionProfile.userId, isInterested);
      }
      
      // Auto-close after a brief delay to show the response
      setTimeout(() => {
        onBackToChatBoxForCSS();
      }, 2000);

    } catch (error) {
      console.error("Error processing interest response:", error);
      
      // Always update the UI state locally even if API fails
      setHasResponded(true);
      setResponseType(isInterested ? 'accept' : 'reject');
      
      // Notify parent component about the response
      if (onProfileResponse) {
        console.log("[profileinterest debug] 🔄 SuggestionProfile calling onProfileResponse (error fallback) with userId:", suggestionProfile.userId, "isInterested:", isInterested);
        onProfileResponse(suggestionProfile.userId, isInterested);
      }
      
      // Close after showing response
      setTimeout(() => {
        onBackToChatBoxForCSS();
      }, 2000);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="suggestion-profile-container">
      <div className="suggestion-profile-header">
        <div
          onClick={onBackToChatBoxForCSS}
          className="chatbox-header-back-btn"
        >
          ←|
        </div>
        <h2 className="suggestion-profile-title">Would you like to connect?</h2>
      </div>

      <div className="suggestion-profile-content">
        {/* Image section with swipeable views */}
        <div className="suggestion-profile-image-section">
          <SwipeableViews
            enableMouseEvents
            index={currentIndex}
            onChangeIndex={setCurrentIndex}
          >
            {suggestionProfile?.pics?.map((pic, index) => (
              <div key={index} className="suggestion-profile-image-container">
                <img
                  src={encodeURI(pic)}
                  alt={`${suggestionProfile.name} ${index + 1}`}
                  className="suggestion-profile-image"
                />
                <div className="suggestion-profile-nav-controls">
                  <div
                    onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                    className="suggestion-profile-nav-arrow left"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </div>
                  <div
                    onClick={() => setCurrentIndex(Math.min(suggestionProfile.pics.length - 1, currentIndex + 1))}
                    className="suggestion-profile-nav-arrow right"
                  >
                    <FontAwesomeIcon icon={faArrowRight} />
                  </div>
                </div>
              </div>
            ))}
          </SwipeableViews>
          
          {/* Image indicators */}
          {suggestionProfile?.pics?.length > 1 && (
            <div className="suggestion-profile-indicators">
              {suggestionProfile.pics.map((_, index) => (
                <div
                  key={index}
                  className={`suggestion-profile-indicator ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => setCurrentIndex(index)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Profile info section */}
        <div className="suggestion-profile-info">
          <div className="suggestion-profile-basic-info">
            <h2 className="suggestion-profile-name">{suggestionProfile.name}, {suggestionProfile.age}</h2>
            {suggestionProfile.milesAway != null && (
              <p className="suggestion-profile-location">📍 {suggestionProfile.milesAway} {suggestionProfile.milesAway === 1 ? 'mile' : 'miles'} away</p>
            )}
          </div>
          
          <div className="suggestion-profile-bio-section">
            <h3 className="suggestion-profile-bio-header">About {suggestionProfile.name}</h3>
            <p className="suggestion-profile-bio">{suggestionProfile.bio}</p>
          </div>
        </div>
      </div>

      {/* Action buttons - matching app style */}
      <div className="suggestion-profile-actions">
        {hasResponded ? (
          <div className={`suggestion-profile-response-message ${responseType === 'accept' ? 'accepted' : 'rejected'}`}>
            {responseType === 'accept' ? (
              <>
                <div className="response-icon">✓</div>
                <p>You've expressed interest in {suggestionProfile.name}!</p>
                <p className="sub-text">We'll reach out to see if they want to connect too.</p>
              </>
            ) : (
              <>
                <div className="response-icon">–</div>
                <p>You've indicated you're not interested in {suggestionProfile.name}.</p>
                <p className="sub-text">Your preference has been saved.</p>
              </>
            )}
          </div>
        ) : (
          <div className="suggestion-profile-action-buttons">
            <button
              onClick={() => handleInterestResponse(false)}
              disabled={isProcessing}
              className="suggestion-profile-action-btn reject"
            >
              <FontAwesomeIcon icon={faTimes} />
              <span>Not interested</span>
            </button>
            <button
              onClick={() => {
                console.log("BASIC TEST: Accept button clicked");
                handleInterestResponse(true);
              }}
              disabled={isProcessing}
              className="suggestion-profile-action-btn accept"
            >
              <FontAwesomeIcon icon={faCheck} />
              <span>Yes, connect!</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuggestionProfile;